import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useNavigate,
  useLocation,
} from "react-router-dom";
import SeoManager from "./components/SeoManager/SeoManager";
import LandingPage from "./components/LandingPage/LandingPage";
import WelcomeScreen from "./components/WelcomeScreen/WelcomeScreen";
import LoginWelcome from "./components/LoginWelcome/LoginWelcome";
import KanbanDashboard from "./components/KanbanDashboard/KanbanDashboard";
import AdminDashboard from "./components/AdminDashboard/AdminDashboard";
import SuperAdminDashboard from "./components/SuperAdminDashboard/SuperAdminDashboard";
import SuperAdminDashboardDemo from "./components/SuperAdminDashboard/SuperAdminDashboardDemo";
import VerifyAccount from "./components/VerifyAccount/VerifyAccount";
import VerifyEmail from "./components/VerifyEmail/VerifyEmail";
import PasswordResetContainer from "./components/PasswordResetContainer/PasswordResetContainer";
import TaskPage from "./components/TaskPage/TaskPage";
import { SignupData } from "./components/SignupForm/SignupForm";
import { LoginUserData } from "./components/LoginForm/LoginForm";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { NotificationProvider } from "./components/NotificationSystem/NotificationSystem";
import { PayPalScriptProvider } from "@paypal/react-paypal-js";
import VerificationMessage from "./components/VerificationMessage/VerificationMessage";

type AppState = "landing" | "welcome" | "loginWelcome" | "dashboard";

function AppContent() {
  const [currentView, setCurrentView] = useState<AppState>("landing");
  const [signupData, setSignupData] = useState<SignupData | null>(null);
  const [currentUser, setCurrentUser] = useState<LoginUserData | null>(null);
  const [showVerificationMessage, setShowVerificationMessage] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Restore user session if possible
  useEffect(() => {
    // Restore session on reload
    const restoreSession = async () => {
      const accessToken = localStorage.getItem("accessToken");
      if (accessToken && !currentUser) {
        try {
          // Use realAPI to fetch user
          const user = await require("./services/realAPI").realAPI.apiRequest(
            "/api/v1/users/me"
          );
          if (user && user.emailVerified) {
            setCurrentUser(user);
            localStorage.setItem("currentUser", JSON.stringify(user));
            setCurrentView("dashboard");
          } else if (user) {
            setCurrentUser(user);
            localStorage.setItem("currentUser", JSON.stringify(user));
            setShowVerificationMessage(true);
          }
        } catch (err) {
          // Invalid token, clear it and show landing page
          console.log("Session expired, clearing tokens");
          localStorage.removeItem("accessToken");
          localStorage.removeItem("refreshToken");
          setCurrentView("landing");
        }
      }
    };
    restoreSession();
    // eslint-disable-next-line
  }, []);

  const handleStartFree = () => {
    setCurrentView("dashboard");
  };

  const handleSignupComplete = (data: SignupData) => {
    setSignupData(data);
    // Instead of going to welcome screen, show verification message
    setCurrentView("landing");
    // You might want to show a modal or message about verification here
  };

  const handleLoginSuccess = (userData: LoginUserData) => {
    setCurrentUser(userData);
    // Store user data in localStorage so other components can access it
    localStorage.setItem("currentUser", JSON.stringify(userData));

    if (!userData.emailVerified) {
      // Show message that email needs verification
      // Set a state to show verification message
      setShowVerificationMessage(true);
      return;
    }
    setCurrentView("dashboard");
    // Honor redirect param if present (e.g., /login?redirect=/tasks/72)
    const params = new URLSearchParams(location.search);
    const redirect = params.get("redirect");
    if (redirect) {
      navigate(redirect, { replace: true });
    } else {
      // Navigate to home route to show the dashboard
      navigate("/");
    }
  };

  const handleGetStarted = () => {
    setCurrentView("dashboard");
  };

  const handleLoginContinue = () => {
    setCurrentView("dashboard");
  };

  const handleLogout = () => {
    // Clear tokens and user data from localStorage
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("currentUser");
    localStorage.removeItem("organizationData");
    setCurrentUser(null);
    setSignupData(null);
    setCurrentView("landing");
    navigate("/");
  };

  return (
    <NotificationProvider>
      <div>
        {/* Add this near the top of your return */}
        {showVerificationMessage && currentUser && (
          <VerificationMessage
            email={currentUser.email}
            onClose={() => setShowVerificationMessage(false)}
          />
        )}
        <Routes>
          {/* Task detail route */}
          <Route path="/tasks/:id" element={<TaskPage />} />
          <Route
            path="/verify/:code"
            element={
              <VerifyAccount
                onSuccess={() => {
                  if (currentUser) {
                    const updatedUser = { ...currentUser, emailVerified: true };
                    setCurrentUser(updatedUser);
                    localStorage.setItem(
                      "currentUser",
                      JSON.stringify(updatedUser)
                    );
                  }
                  navigate("/login");
                }}
              />
            }
          />

          <Route
            path="/verify-email"
            element={
              <VerifyEmail
                onSuccess={() => {
                  if (currentUser) {
                    const updatedUser = { ...currentUser, emailVerified: true };
                    setCurrentUser(updatedUser);
                    localStorage.setItem(
                      "currentUser",
                      JSON.stringify(updatedUser)
                    );
                  }
                  navigate("/login");
                }}
              />
            }
          />

          <Route
            path="/forgot-password"
            element={<PasswordResetContainer type="forgot" />}
          />
          <Route
            path="/reset-password"
            element={<PasswordResetContainer type="reset" />}
          />

          <Route
            path="/login"
            element={
              <LandingPage
                onStartFree={handleStartFree}
                onSignupComplete={handleSignupComplete}
                onLoginSuccess={handleLoginSuccess}
                showLoginByDefault={true}
              />
            }
          />

          {/* Development Routes for Super Admin Dashboard Testing */}
          <Route
            path="/super-admin"
            element={<SuperAdminDashboard onLogout={handleLogout} />}
          />

          <Route
            path="/super-admin-demo"
            element={<SuperAdminDashboardDemo />}
          />

          <Route
            path="/"
            element={
              <>
                {currentView === "welcome" && signupData && (
                  <WelcomeScreen
                    signupData={signupData}
                    onGetStarted={handleGetStarted}
                  />
                )}

                {currentView === "loginWelcome" && currentUser && (
                  <LoginWelcome
                    userData={currentUser}
                    onContinue={handleLoginContinue}
                  />
                )}

                {currentView === "dashboard" &&
                  currentUser &&
                  currentUser.role === "SUPER_ADMIN" &&
                  // Check for ?demo=true query parameter or use real version
                  (new URLSearchParams(window.location.search).get("demo") ===
                  "true" ? (
                    <SuperAdminDashboardDemo />
                  ) : (
                    <SuperAdminDashboard onLogout={handleLogout} />
                  ))}

                {currentView === "dashboard" &&
                  currentUser &&
                  currentUser.role === "ADMIN" && (
                    <AdminDashboard
                      currentUser={currentUser}
                      onLogout={handleLogout}
                    />
                  )}

                {currentView === "dashboard" &&
                  currentUser &&
                  currentUser.role !== "ADMIN" &&
                  currentUser.role !== "SUPER_ADMIN" && (
                    <DndProvider backend={HTML5Backend}>
                      <KanbanDashboard
                        currentUser={currentUser}
                        onLogout={handleLogout}
                      />
                    </DndProvider>
                  )}

                {currentView === "landing" && (
                  <LandingPage
                    onStartFree={handleStartFree}
                    onSignupComplete={handleSignupComplete}
                    onLoginSuccess={handleLoginSuccess}
                  />
                )}
              </>
            }
          />
        </Routes>
      </div>
    </NotificationProvider>
  );
}

function App() {
  return (
    <PayPalScriptProvider
      deferLoading={!process.env.REACT_APP_PAYPAL_CLIENT_ID}
      options={{
        // IMPORTANT: Set REACT_APP_PAYPAL_CLIENT_ID in your environment (.env)
        // Do not hardcode credentials in the repository.
        clientId: process.env.REACT_APP_PAYPAL_CLIENT_ID as string,
        currency: "USD",
        intent: "capture",
        components: "buttons",
        "enable-funding": "card",
        "disable-funding":
          "credit,bancontact,blik,eps,giropay,ideal,mercadopago,mybank,p24,sepa,sofort,venmo",
      }}
    >
      <Router>
        <SeoManager />
        <AppContent />
      </Router>
    </PayPalScriptProvider>
  );
}

export default App;
